import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wallet, ArrowUpRight, ArrowDownRight, TrendingUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { PeriodSelector, KPICard } from "@/components/UIComponents";
import { formatCurrency, MONTHS, CHART_COLORS } from "@/lib/config";
import api from "@/lib/api";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="font-medium text-slate-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={entry.id || entry._id || index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="money-value">{formatCurrency(entry.value)}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const CashFlow = () => {
  const [year, setYear] = useState(2025);
  const [month, setMonth] = useState(1);
  const [loading, setLoading] = useState(true);
  const [cashflow, setCashflow] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [cf, monthly] = await Promise.all([
          api.getCashflowBancaire(year, month),
          api.getAnalyseMensuelle(year)
        ]);
        setCashflow(cf);
        
        // Calculer le cashflow cumulé pour chaque mois
        let cumul = 0;
        const cashflowData = monthly.map(m => {
          cumul += m.profit;
          return {
            ...m,
            solde: cumul + 50000 // Solde de base
          };
        });
        setMonthlyData(cashflowData);
      } catch (error) {
        console.error("Error:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [year, month]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  return (
    <div data-testid="cashflow-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Cash Flow Bancaire</h1>
          <p className="text-slate-500 mt-1 text-sm">{MONTHS[month]} {year}</p>
        </div>
        <PeriodSelector year={year} setYear={setYear} month={month} setMonth={setMonth} />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in delay-100">
        <KPICard
          title="Solde Initial"
          value={formatCurrency(cashflow?.solde_initial)}
          subtitle="Début du mois"
          icon={Wallet}
          color="blue"
        />
        <KPICard
          title="Entrées"
          value={formatCurrency(cashflow?.entrees)}
          subtitle="Revenus reçus"
          icon={ArrowUpRight}
          color="emerald"
        />
        <KPICard
          title="Sorties"
          value={formatCurrency(cashflow?.sorties)}
          subtitle="Dépenses payées"
          icon={ArrowDownRight}
          color="red"
        />
        <KPICard
          title="Solde Final"
          value={formatCurrency(cashflow?.solde_final)}
          subtitle={`Variation: ${formatCurrency(cashflow?.variation)}`}
          change={cashflow?.variation >= 0 ? "Positif" : "Négatif"}
          changeType={cashflow?.variation >= 0 ? 'positive' : 'negative'}
          icon={TrendingUp}
          color={cashflow?.variation >= 0 ? "emerald" : "red"}
        />
      </div>

      {/* Graphique évolution */}
      <Card className="animate-fade-in delay-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Évolution du Solde Bancaire - {year}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="colorSolde" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="mois_nom" tick={{ fontSize: 11 }} tickFormatter={(v) => v.substring(0, 3)} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v/1000}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="solde" name="Solde" stroke="#0EA5E9" fillOpacity={1} fill="url(#colorSolde)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Détail du mois */}
      <Card className="animate-fade-in delay-300">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Détail du Flux - {MONTHS[month]} {year}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <span className="font-medium text-blue-700">Solde au début du mois</span>
              <span className="kpi-value text-xl text-blue-800">{formatCurrency(cashflow?.solde_initial)}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
              <span className="font-medium text-emerald-700">+ Entrées (Revenus)</span>
              <span className="kpi-value text-xl text-emerald-800">+{formatCurrency(cashflow?.entrees)}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <span className="font-medium text-red-700">- Sorties (Dépenses)</span>
              <span className="kpi-value text-xl text-red-800">-{formatCurrency(cashflow?.sorties)}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-100 rounded-lg border-2 border-slate-300">
              <span className="font-bold text-slate-800">= Solde à la fin du mois</span>
              <span className="kpi-value text-2xl text-slate-900">{formatCurrency(cashflow?.solde_final)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CashFlow;
